import { useCallback } from 'react';
import { Flame, CheckCircle2 } from 'lucide-react';
import { useSystemStore } from '@/stores/system';
import { useChannel } from '@/realtime/use-channel';
import { ProgressBar } from '@/components/widgets/ProgressBar';
import { Button } from '@/components/ui/button';
import type { SystemEvent } from '@/realtime/types';

/**
 * 强平进度全屏遮罩 — 订阅 system 频道。
 *
 * 显示规则:
 * - 收到 force_liquidation_started → 显示遮罩(进度 0)
 * - 收到 force_liquidation_progress → 更新进度条
 * - 收到 force_liquidation_completed → 切换为成功界面 + [关闭] 让管理员手动关闭
 *
 * 这是 §8.4.6 步骤 6-7 的可视化:"实时显示进度: 主账户已平仓 ✓ / 雇员 1 已平仓 ✓ / ..."
 */
export function LiquidationOverlay() {
  const liquidation = useSystemStore((s) => s.liquidation);
  const setSessionState = useSystemStore((s) => s.setSessionState);
  const setTradeLock = useSystemStore((s) => s.setTradeLock);
  const setLiquidation = useSystemStore((s) => s.setLiquidation);

  const handleSystemEvent = useCallback(
    (ev: SystemEvent) => {
      switch (ev.type) {
        case 'force_liquidation_started':
          setLiquidation({ active: true, closed: 0, failed: 0, total: undefined, triggeredBy: ev.triggeredBy });
          setSessionState('LIQUIDATING');
          break;
        case 'force_liquidation_progress':
          setLiquidation({
            active: true,
            closed: ev.positionsClosed,
            total: ev.total,
          });
          break;
        case 'force_liquidation_completed':
          setLiquidation({
            active: false,
            closed: ev.positionsClosed,
            failed: ev.positionsFailed,
            total: ev.positionsClosed + ev.positionsFailed,
            emergencyStopHeld: ev.emergencyStopHeld,
          });
          setSessionState('CLOSED');
          setTradeLock(true);
          break;
        case 'session_state_changed':
          setSessionState(ev.to);
          break;
        case 'trade_lock_changed':
          setTradeLock(ev.locked, ev.unlockAt ?? null);
          break;
        case 'emergency_stop_set':
          useSystemStore.getState().setEmergencyStop(true, ev.reason, ev.triggeredBy);
          break;
        case 'emergency_stop_cleared':
          useSystemStore.getState().setEmergencyStop(false);
          useSystemStore.getState().setTradeLock(false);
          break;
        default:
          break;
      }
    },
    [setLiquidation, setSessionState, setTradeLock],
  );

  useChannel('system', handleSystemEvent, [handleSystemEvent]);

  if (!liquidation.active && !(liquidation.total && liquidation.closed != null)) {
    return null;
  }

  const total = liquidation.total ?? 1;
  const closed = liquidation.closed ?? 0;
  const failed = liquidation.failed ?? 0;
  const pct = total > 0 ? ((closed + failed) / total) * 100 : 0;
  const finished = !liquidation.active && total > 0 && closed + failed >= total;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-0/70 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-lg border border-line bg-bg-1 p-6 shadow-2xl elev-elev">
        <div className="flex items-center gap-3">
          {finished ? (
            <CheckCircle2 className="h-7 w-7 text-up" />
          ) : (
            <Flame className="h-7 w-7 text-down animate-pulse-glow" />
          )}
          <div>
            <div className="text-base font-semibold text-ink-0">{finished ? '强平完成' : '强平进行中...'}</div>
            <div className="text-xs text-ink-2">
              {finished
                ? '系统已进入 CLOSED 状态,交易锁定至次日开仓时段'
                : '请勿关闭页面,后端正在按 §7.5.2 顺序执行'}
            </div>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <ProgressBar
            value={pct}
            tone={finished ? 'up' : 'down'}
            label={`已完成 ${closed + failed} / ${total}`}
            showVal={false}
          />
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-md border border-line bg-bg-3 p-3">
              <div className="text-xs text-ink-2">已平仓位</div>
              <div className="num text-lg font-semibold">{closed}</div>
            </div>
            <div className="rounded-md border border-line bg-bg-3 p-3">
              <div className="text-xs text-ink-2">平仓失败</div>
              <div className={`num text-lg font-semibold ${failed > 0 ? 'text-down' : ''}`}>{failed}</div>
            </div>
          </div>
          {finished && liquidation.emergencyStopHeld && (
            <div className="rounded-md border border-down/40 bg-down/10 p-3 text-xs text-down">
              真实账户仍有未平仓位,已保持紧急停机,需人工处置后再解除。
            </div>
          )}
        </div>

        {finished && (
          <div className="mt-5 flex justify-end">
            <Button variant="default" onClick={() => setLiquidation({ active: false, closed: 0, failed: 0, total: undefined, emergencyStopHeld: false })}>
              关闭
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
